import moment from "moment";
import { IconBullet } from "../Icons";
import {
  getNameClientById,
  getNomeDaAgenciaPorId,
  getNomeStatusPorId,
} from "../../utils/getNamesById";
import { ExpandableText } from "./ExpandableText";
import { useTimelineData } from "./hooks/useTimelineData";
import {
  formatarNumeroPadrao,
  formatarValorEmRealBR,
  safeJsonParse,
} from "@/components/utils/Utils";
import { useTranslation } from "react-i18next";

export function TimelineDesk({ data }: any) {
  const { t } = useTranslation();
  const { status, clients, agencies, representatives } = useTimelineData();

  function getEntityTimelineLabel(action: "created" | "edited" | "deleted" | "status") {
    if (data?.type === "campanha") {
      const labels = {
        created: "Campanha criada",
        edited: "Campanha editada",
        deleted: "Campanha excluída",
        status: "Status da campanha alterado",
      };
      return labels[action];
    }

    if (data?.type === "prospecting") {
      const labels = {
        created: t("userGoOn:timeline.prospecting_created"),
        edited: t("userGoOn:timeline.prospecting_edited"),
        deleted: t("userGoOn:timeline.prospecting_deleted"),
        status: t("userGoOn:timeline.prospecting_status_changed"),
      };
      return labels[action];
    }

    const labels = {
      created: t("userGoOn:timeline.desk_created"),
      edited: t("userGoOn:timeline.desk_edited"),
      deleted: t("userGoOn:timeline.desk_deleted"),
      status: t("userGoOn:timeline.desk_status_changed"),
    };
    return labels[action];
  }

  // Função auxiliar para obter nome do representante
  function getNomeRepresentantePorId(id: number) {
    if (!representatives || !Array.isArray(representatives))
      return t("userGoOn:timeline.representative_not_found");
    const representante = representatives.find((rep: any) => rep.id === id);
    return (
      representante?.name_fantasy ||
      representante?.nome ||
      t("userGoOn:timeline.representative_not_found")
    );
  }

  // Função auxiliar para obter nome do representado
  function getNomeRepresentadoPorId(_id: number) {
    if (!data.desk?.representado)
      return t("userGoOn:timeline.represented_not_found");
    return (
      data.desk.representado.name_fantasy ||
      data.desk.representado.nome ||
      t("userGoOn:timeline.represented_not_found")
    );
  }

  // Função auxiliar para normalizar valores para comparação
  function normalizeValue(value: any): string {
    if (value === null || value === undefined || value === "null") return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }

  // Função auxiliar para verificar se dois valores são realmente diferentes
  function areValuesDifferent(oldVal: any, newVal: any): boolean {
    const normalizedOld = normalizeValue(oldVal);
    const normalizedNew = normalizeValue(newVal);
    return normalizedOld !== normalizedNew;
  }

  function encontrarMudancas(objeto: any) {
    const oldObj = objeto.old && JSON.parse(objeto.old);
    const newObj = objeto.new && JSON.parse(objeto.new);
    const mudancas: any = {};

    const nomePropriedades: any = {
      date_end: "Data Final",
      date_init: "Data Inicial",
      id_responsavel: "Responsável",
      status: "Status",
      title: "Título",
      date: "Data",
      description: "Descrição",
      status_id: "Status",
      proposta: "Proposta",
      tipo_representacao_id: "Tabela Representação",
      cliente_id: "Cliente",
      agency_id: "Agência",
      numero_pi: "Numero PI",
      id_representante: "Representante",
      id_representado: "Representado",
      name: "Nome da Desk",
      objetivo: "Objetivo",
      praca: "Praça",
      publico: "Público",
      links: "Links",
      valor_proposta: "Valor da Proposta",
      produto: "Produto",
      referencias: "Referências",
      time_agency: "Time da Agência",
      time_goon: "Time Go On",
      entregues: "Entregues",
      vtrs: "VTRs",
    };

    // Criar cópias dos objetos para não modificar os originais
    const oldCopy = { ...oldObj };
    const newCopy = { ...newObj };

    Object.keys(newCopy).forEach((chave) => {
      const nomeEmPortugues = nomePropriedades[chave] || chave;

      // Processar datas
      if (chave === "date_end" || chave === "date_init") {
        if (oldCopy[chave]) {
          oldCopy[chave] = moment.utc(oldCopy[chave]).format("DD/MM/YYYY");
        }
        if (newCopy[chave]) {
          newCopy[chave] = moment.utc(newCopy[chave]).format("DD/MM/YYYY");
        }
      }

      // Processar status usando dados da desk se disponível
      if (chave === "status_id") {
        if (oldCopy[chave] && status) {
          oldCopy[chave] = getNomeStatusPorId(status, Number(oldCopy[chave]));
        }
        if (newCopy[chave] && status) {
          newCopy[chave] = getNomeStatusPorId(status, Number(newCopy[chave]));
        }
      }

      // Processar cliente usando dados da desk se disponível
      if (chave === "cliente_id") {
        if (data.desk?.cliente?.name_fantasy) {
          oldCopy[chave] = data.desk.cliente.name_fantasy;
          newCopy[chave] = data.desk.cliente.name_fantasy;
        } else {
          if (oldCopy[chave]) {
            oldCopy[chave] = getNameClientById(clients, Number(oldCopy[chave]));
          }
          if (newCopy[chave]) {
            newCopy[chave] = getNameClientById(clients, Number(newCopy[chave]));
          }
        }
      }

      // Processar agência usando dados da desk se disponível
      if (chave === "agency_id") {
        if (data.desk?.agencia?.name_fantasy) {
          oldCopy[chave] = data.desk.agencia.name_fantasy;
          newCopy[chave] = data.desk.agencia.name_fantasy;
        } else {
          if (oldCopy[chave]) {
            oldCopy[chave] = getNomeDaAgenciaPorId(
              agencies,
              Number(oldCopy[chave]),
            );
          }
          if (newCopy[chave]) {
            newCopy[chave] = getNomeDaAgenciaPorId(
              agencies,
              Number(newCopy[chave]),
            );
          }
        }
      }

      // Processar representante usando dados da desk se disponível
      if (chave === "id_representante") {
        if (data.desk?.representante?.name_fantasy) {
          oldCopy[chave] = data.desk.representante.name_fantasy;
          newCopy[chave] = data.desk.representante.name_fantasy;
        } else {
          if (oldCopy[chave]) {
            oldCopy[chave] = getNomeRepresentantePorId(Number(oldCopy[chave]));
          }
          if (newCopy[chave]) {
            newCopy[chave] = getNomeRepresentantePorId(Number(newCopy[chave]));
          }
        }
      }

      // Processar representado usando dados da desk se disponível
      if (chave === "id_representado") {
        if (data.desk?.representado?.name_fantasy) {
          oldCopy[chave] = data.desk.representado.name_fantasy;
          newCopy[chave] = data.desk.representado.name_fantasy;
        } else {
          if (oldCopy[chave]) {
            oldCopy[chave] = getNomeRepresentadoPorId(Number(oldCopy[chave]));
          }
          if (newCopy[chave]) {
            newCopy[chave] = getNomeRepresentadoPorId(Number(newCopy[chave]));
          }
        }
      }

      // Processar links
      if (chave === "links") {
        if (oldCopy[chave] && typeof oldCopy[chave] === "object") {
          oldCopy[chave] = JSON.stringify(oldCopy[chave]);
        }
        if (newCopy[chave] && typeof newCopy[chave] === "object") {
          newCopy[chave] = JSON.stringify(newCopy[chave]);
        }
      }

      // Ignorar campos que são nulos/vazios em ambos os lados
      if (
        (chave === "funil_id" ||
          chave === "links" ||
          chave === "numero_pi" ||
          chave === "time_agency" ||
          chave === "time_goon" ||
          chave === "valor_proposta" ||
          chave === "produto" ||
          chave === "referencias" ||
          chave === "entregues" ||
          chave === "vtrs") &&
        normalizeValue(oldCopy[chave]) === "" &&
        normalizeValue(newCopy[chave]) === ""
      ) {
        return;
      }

      if (chave === "vendedores") {
        return;
      }

      // Verificar se realmente houve mudança
      if (areValuesDifferent(oldCopy[chave], newCopy[chave])) {
        mudancas[nomeEmPortugues] = {
          antigo: oldCopy[chave] || "Vazio",
          novo: newCopy[chave] || "Vazio",
          Status_Desk: "Diferentes",
        };
      }
    });
    return mudancas;
  }

  return (
    <>
      <div className="hidden sm:flex flex-col gap-1 items-center">
        <div className="w-[2px] h-full bg-black rounded-lg" />
        <IconBullet />
        <div className="w-[2px] h-full bg-black rounded-lg" />
      </div>
      <div className="bg-cinza-100 rounded-3xl p-4 flex flex-col gap-1 text-sm mb-4 w-full">
        <h3 className="text-base">
          <b>
            {data.alteracao === "created" && getEntityTimelineLabel("created")}
            {data.alteracao === "update" && getEntityTimelineLabel("edited")}
            {(data.alteracao === "delete" || data.alteracao === "deleted") &&
              getEntityTimelineLabel("deleted")}
            {data.alteracao === "change status" &&
              getEntityTimelineLabel("status")}
          </b>
        </h3>
        <label className="flex items-center gap-1">
          <span>{t("userGoOn:timeline.responsible")}</span>
          <div className="flex gap-2 items-center">
            <b>
              {data?.user?.name} {data?.user?.last_name}
            </b>
            <img
              src={data?.user?.url_img}
              alt={data?.user?.name}
              className="w-6 h-6 rounded-full object-cover"
            />
          </div>
        </label>

        {data.alteracao === "update" && (
          <>
            <label>
              <span>{t("userGoOn:timeline.description")}</span>
            </label>
            <div className="bg-white rounded-3xl p-4">
              {Object.keys(encontrarMudancas(data)).map((key) => (
                <p key={key} className="mb-2">
                  <div className="">
                    {key === "Proposta" ? (
                      <div>
                        {t("userGoOn:timeline.changed")} <b>{key} </b>{" "}
                        {t("userGoOn:timeline.from")}:
                        <table className="w-full text-center my-1 text-xs">
                          <tr className="bg-azul-400 text-white">
                            <td>{t("userGoOn:desks.table_list.vehicle")}</td>
                            <td>{t("userGoOn:timeline.channel")}</td>
                            <td>{t("userGoOn:desks.table.models")}</td>
                            <td>{t("userGoOn:timeline.quantity")}</td>
                            <td>
                              {t("userGoOn:desks.table_list.gross_total")}
                            </td>
                          </tr>
                          {Array.isArray(
                            safeJsonParse(encontrarMudancas(data)[key].antigo),
                          ) &&
                            safeJsonParse(
                              encontrarMudancas(data)[key].antigo,
                            )?.map((e: any) => (
                              <tr>
                                <td>{e.veiculo_name ?? e.veiculo ?? "-"}</td>
                                <td>{e.veiculo_canalName ?? "-"}</td>
                                <td>
                                  {e.veiculo_modeloName ?? e.nameModelo ?? "-"}
                                </td>
                                <td>
                                  {e.qtd || e?.insercaoTotal
                                    ? formatarNumeroPadrao(
                                        Number(e.qtd ?? e?.insercaoTotal),
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {e.total || e.totalTabela
                                    ? formatarValorEmRealBR(
                                        e.total ?? e.totalTabela,
                                      )
                                    : "-"}
                                </td>
                              </tr>
                            ))}{" "}
                        </table>
                        {t("userGoOn:timeline.to")}:
                        <table className="w-full text-center my-1 text-xs">
                          <tr className="bg-azul-400 text-white">
                            <td>{t("userGoOn:desks.table_list.vehicle")}</td>
                            <td>{t("userGoOn:timeline.channel")}</td>
                            <td>{t("userGoOn:desks.table.models")}</td>
                            <td>{t("userGoOn:timeline.quantity")}</td>
                            <td>
                              {t("userGoOn:desks.table_list.gross_total")}
                            </td>
                          </tr>
                          {Array.isArray(
                            safeJsonParse(encontrarMudancas(data)[key].novo),
                          ) &&
                            safeJsonParse(
                              encontrarMudancas(data)[key].novo,
                            ).map((e: any) => (
                              <tr>
                                <td>{e.veiculo_name ?? e.veiculo ?? "-"}</td>
                                <td>{e.veiculo_canalName ?? "-"}</td>
                                <td>
                                  {e.veiculo_modeloName ?? e.nameModelo ?? "-"}
                                </td>
                                <td>
                                  {e.qtd || e.insercaoTotal
                                    ? formatarNumeroPadrao(
                                        Number(e.qtd ?? e.insercaoTotal),
                                      )
                                    : "-"}
                                </td>
                                <td>
                                  {e.total || e.totalTabela
                                    ? formatarValorEmRealBR(
                                        e.total ?? e.totalTabela,
                                      )
                                    : "-"}
                                </td>
                              </tr>
                            ))}{" "}
                        </table>
                      </div>
                    ) : key === "links" ? (
                      <p>
                        {t("userGoOn:timeline.changed")} <b>{key}</b>{" "}
                        {t("userGoOn:timeline.from")}{" "}
                        {encontrarMudancas(data)[key].antigo &&
                        encontrarMudancas(data)[key].antigo !==
                          t("userGoOn:timeline.empty") &&
                        encontrarMudancas(data)[key].antigo !== "" ? (
                          (() => {
                            try {
                              const antigo = JSON.parse(
                                encontrarMudancas(data)[key].antigo,
                              );
                              return Array.isArray(antigo) &&
                                antigo.length > 0 ? (
                                antigo.map((e: any, index: number) => (
                                  <span key={index} className="block truncate">
                                    <a
                                      href={typeof e === "string" ? e : e?.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline"
                                    >
                                      {typeof e === "string"
                                        ? e
                                        : e?.link ||
                                          t("userGoOn:timeline.link_no_url")}
                                    </a>
                                  </span>
                                ))
                              ) : (
                                <span>{t("userGoOn:timeline.empty")}</span>
                              );
                            } catch (err) {
                              return (
                                <span>{t("userGoOn:timeline.empty")}</span>
                              );
                            }
                          })()
                        ) : (
                          <span>{t("userGoOn:timeline.empty")}</span>
                        )}{" "}
                        {t("userGoOn:timeline.to")}{" "}
                        {encontrarMudancas(data)[key]?.novo &&
                        encontrarMudancas(data)[key]?.novo !==
                          t("userGoOn:timeline.empty") &&
                        encontrarMudancas(data)[key]?.novo !== "" ? (
                          (() => {
                            try {
                              const novo = safeJsonParse(
                                encontrarMudancas(data)[key]?.novo,
                              );
                              return Array.isArray(novo) && novo.length > 0 ? (
                                novo.map((e: any, index: number) => (
                                  <span key={index} className="block truncate">
                                    <a
                                      href={typeof e === "string" ? e : e?.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline"
                                    >
                                      {typeof e === "string"
                                        ? e
                                        : e?.link ||
                                          t("userGoOn:timeline.link_no_url")}
                                    </a>
                                  </span>
                                ))
                              ) : (
                                <span>{t("userGoOn:timeline.empty")}</span>
                              );
                            } catch (err) {
                              return (
                                <span>{t("userGoOn:timeline.empty")}</span>
                              );
                            }
                          })()
                        ) : (
                          <span>{t("userGoOn:timeline.empty")}</span>
                        )}
                      </p>
                    ) : (
                      <div>
                        {t("userGoOn:timeline.changed")} <b>{key} </b>{" "}
                        {t("userGoOn:timeline.from")}{" "}
                        <span>
                          <ExpandableText
                            content={
                              encontrarMudancas(data)[key].antigo ||
                              `<p>${t("userGoOn:timeline.no_information")}</p>`
                            }
                            maxLength={150}
                          />
                        </span>{" "}
                        {t("userGoOn:timeline.to")}{" "}
                        <span>
                          <ExpandableText
                            content={
                              encontrarMudancas(data)[key].novo ||
                              `<p>${t("userGoOn:timeline.no_information")}</p>`
                            }
                            maxLength={150}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                </p>
              ))}
            </div>
          </>
        )}

        {data.alteracao === "deleted" && (
          <div className="bg-white rounded-3xl p-4">
            <p>
              <b>
                {data?.user?.name} {data?.user?.last_name}
              </b>{" "}
              {data?.type === "campanha"
                ? "excluiu a campanha em"
                : data?.type === "prospecting"
                ? t("userGoOn:timeline.prospecting_deleted_at")
                : t("userGoOn:timeline.desk_deleted_at")}{" "}
              <b>{moment(data.createdAt).format("DD/MM/YYYY")}</b>{" "}
              {t("userGoOn:timeline.at")}{" "}
              <b>{moment(data.createdAt).format("HH:mm")}</b>
            </p>
          </div>
        )}

        {data.alteracao === "change status" && (
          <div className="bg-white rounded-3xl p-4">
            <p>
              <span>
                {data?.type === "campanha"
                  ? "Status da campanha alterado para"
                  : t("userGoOn:timeline.desk_status_changed_to")}
              </span>{" "}
              <span>
                {" "}
                <b>
                  {status
                    ? getNomeStatusPorId(
                        status,
                        data?.new &&
                          (JSON.parse(data?.new)?.status_id ??
                            JSON.parse(data?.new)),
                      )
                    : t("common:loading")}
                </b>
              </span>
            </p>
          </div>
        )}
        <span className="text-xs text-cinza-800">
          {t("userGoOn:timeline.created_at")}:{" "}
          {moment(data.createdAt).format("DD/MM/YYYY - HH:mm")}
        </span>
      </div>
    </>
  );
}
